import posthog from 'posthog-js';
import { getConsent, getReplayConsent } from './consent';
import { isAdminRoute, isSensitiveRoute, isPublicMarketingRoute } from './sensitive-routes';
import { isUserObjected } from './user-optout';

declare global {
  interface Window {
    __otalyticsPosthogInitialized?: boolean;
  }
}

function hasWindow(): boolean {
  return typeof window !== 'undefined';
}

export function isPosthogLoaded(): boolean {
  return (posthog as { __loaded?: boolean }).__loaded === true;
}

function isLocalHostname(hostname: string): boolean {
  return (
    hostname === "localhost" ||
    hostname === "127.0.0.1" ||
    hostname === "::1" ||
    hostname.endsWith(".local")
  );
}

export const POSTHOG_US_INGEST_HOST = "https://us.i.posthog.com";

export const initPostHog = (): void => {
  if (!hasWindow()) {
    return;
  }

  if (window.__otalyticsPosthogInitialized) {
    return;
  }

  if (isPosthogLoaded()) {
    window.__otalyticsPosthogInitialized = true;
    return;
  }

  // Set first to protect against StrictMode double-invocation races in dev.
  window.__otalyticsPosthogInitialized = true;

  const posthogKey = process.env.NEXT_PUBLIC_POSTHOG_KEY;
  const posthogHost = process.env.NEXT_PUBLIC_POSTHOG_HOST;

  if (!posthogKey) {
    console.warn('NEXT_PUBLIC_POSTHOG_KEY not configured - PostHog tracking disabled');
    return;
  }

  // Same production project as the API points at, so a local click otherwise
  // lands in the funnels the product is measured on. See the longer note in
  // apps/api/src/modules/posthog/posthog.service.ts.
  //
  // Set NEXT_PUBLIC_POSTHOG_ENABLED_IN_DEV=true to opt a local run back in.
  // A production bundle served from localhost (next start) is blocked by
  // hostname as well: NODE_ENV is production there, which is not enough.
  if (isLocalHostname(window.location.hostname)) {
    return;
  }
  if (
    process.env.NODE_ENV === 'development' &&
    process.env.NEXT_PUBLIC_POSTHOG_ENABLED_IN_DEV !== 'true'
  ) {
    return;
  }

  const host = posthogHost || POSTHOG_US_INGEST_HOST;
  if (/eu\.(i\.)?posthog\.com/.test(host)) {
    console.error(
      `NEXT_PUBLIC_POSTHOG_HOST=${host} is EU cloud; this project is on US cloud (${POSTHOG_US_INGEST_HOST}).`
    );
  }

  // Consent decides persistence BEFORE init. Starting in memory and switching
  // to localStorage+cookie afterwards writes a brand-new distinct id over the
  // cookie (posthog-js clears the previous store and saves the in-memory
  // props). That is why every full page load was a new person.
  const persistence = persistenceForConsent();

  posthog.init(posthogKey, {
    api_host: host,
    capture_pageview: false,
    // Capture nothing until the gate below permits it (see loaded()). This
    // closes the window between init and the first gate evaluation, and makes
    // "no recorded choice / no known state" mean "no analytics" by default.
    opt_out_capturing_by_default: true,
    // Cookie persistence only after analytics consent. Before that, memory:
    // no device storage. See applyPersistence().
    persistence,
    // Session replay is a separate, more invasive ask than analytics. Keep it
    // off at init and only start it when the user explicitly opts in AND the
    // current route is not sensitive (see applyReplay()).
    disable_session_recording: true,
    session_recording: {
      // Mask EVERY input. This is a B2B product where users type competitor
      // names, tour titles, keywords and pricing into forms; recording that
      // verbatim is the sharpest privacy exposure. maskAllInputs already
      // covers passwords and textareas, so a per-input maskInputOptions map
      // would be redundant (and contradictory) — omit it.
      maskAllInputs: true,
      recordCrossOriginIframes: false,
    },
    // Set autocapture explicitly rather than inheriting PostHog's ON default.
    // OFF on public marketing routes (no product signal worth the blanket
    // click/interaction capture); the authenticated side re-enables it (see
    // applyAutocapture()). An explicit, reviewable value beats an implicit
    // default.
    autocapture: false,
    // Automatically include environment in all events
    loaded: (posthog) => {
      posthog.register({
        environment:
          process.env.NEXT_PUBLIC_ENVIRONMENT ||
          (process.env.NODE_ENV === 'production' ? 'production' : 'development'),
      });
      // Apply the full gate once the SDK is ready. Persistence was already
      // chosen at init; applyPersistence is a no-op when it matches.
      applyPersistence(window.location.pathname);
      applyAutocapture(window.location.pathname);
      applyCapture();
      applyReplay(window.location.pathname);
    },
  });
};

function doNotTrackEnabled(): boolean {
  return typeof navigator !== 'undefined' && navigator.doNotTrack === '1';
}

/**
 * The single precedence rule for whether client-side analytics may capture.
 *
 * Three independent inputs gate general capture; ANY ONE saying "no" means no
 * (logical AND). There is no ordering or override — they are peers:
 *
 *   capture allowed  ⟺  NOT Do-Not-Track
 *                        AND cookie consent === 'accepted'
 *                        AND NOT user objected (user-level opt-out)
 *
 *  - Do Not Track: a browser-level signal; always forces off.
 *  - Cookie consent: the ePrivacy device-storage consent from #254; must be
 *    explicitly `accepted` (a decline or no-choice-yet is off).
 *  - User opt-out: the account-level objection from #268; when the user has
 *    objected (`analyticsEnabled === false`) capture is off regardless of the
 *    other two. Absent (pre-#268) it defaults to "not objected".
 *
 * Session replay (see {@link applyReplay}) layers TWO further AND conditions on
 * top of this: explicit replay consent, and a non-sensitive route.
 */
export function isAnalyticsPermitted(): boolean {
  return (
    !doNotTrackEnabled() &&
    getConsent() === 'accepted' &&
    !isUserObjected()
  );
}

/**
 * Apply the general-capture precedence rule to PostHog at runtime. Called on
 * mount and whenever any of its inputs change (consent, replay consent, or the
 * user-level opt-out).
 */
function capturingState(): 'in' | 'out' | 'unknown' {
  const sdk = posthog as {
    has_opted_out_capturing?: () => boolean;
    has_opted_in_capturing?: () => boolean;
  };
  if (typeof sdk.has_opted_out_capturing === 'function' && sdk.has_opted_out_capturing()) {
    return 'out';
  }
  if (typeof sdk.has_opted_in_capturing === 'function' && sdk.has_opted_in_capturing()) {
    return 'in';
  }
  return 'unknown';
}

export const applyCapture = (): void => {
  if (!hasWindow() || !isPosthogLoaded()) {
    return;
  }

  // opt_in_capturing() emits `$opt_in` every call. Route changes re-enter
  // this function; only the transition should hit the SDK.
  const state = capturingState();
  if (isAnalyticsPermitted()) {
    if (state !== 'in') {
      posthog.opt_in_capturing();
    }
  } else if (state !== 'out') {
    posthog.opt_out_capturing();
  }
};

type PersistenceMode = 'memory' | 'localStorage+cookie';

/**
 * Cookies only after the visitor accepts analytics. Declined or not-yet
 * chosen stays in memory, so we never write a device id before consent.
 * After consent the same mode is used on every route, including /reports,
 * /blog and the return from Stripe. Switching per route called set_config,
 * and posthog-js clears the previous store when the mode changes — that
 * wiped the cookie on the way into the app and minted a new person.
 */
function persistenceForConsent(): PersistenceMode {
  return getConsent() === 'accepted' ? 'localStorage+cookie' : 'memory';
}

export const applyPersistence = (_pathname: string | null): void => {
  if (!hasWindow() || !isPosthogLoaded()) {
    return;
  }

  const persistence = persistenceForConsent();
  const current = (posthog as { config?: { persistence?: string } }).config?.persistence;
  if (current === persistence) {
    return;
  }
  posthog.set_config({ persistence });
};

/**
 * Start or stop session replay for the current route.
 *
 * Replay is the most invasive capture, gated on ALL of:
 *  1. general analytics is permitted — {@link isAnalyticsPermitted} (consent +
 *     no DNT + not objected);
 *  2. replay consent is explicitly `accepted` — accepting analytics cookies
 *     does NOT enable recording, being filmed is a separate ask;
 *  3. the current route is not sensitive (settings / pricing / billing /
 *     checkout), where recording is suppressed regardless of choice.
 *
 * Called on mount and on every route change / consent change / opt-out change.
 */
export const applyReplay = (pathname: string | null): void => {
  if (!hasWindow() || !isPosthogLoaded()) {
    return;
  }

  const replayConsented = getReplayConsent() === 'accepted';

  if (isAnalyticsPermitted() && replayConsented && !isSensitiveRoute(pathname)) {
    posthog.startSessionRecording();
  } else {
    posthog.stopSessionRecording();
  }
};

/**
 * Enable autocapture on the authenticated product and disable it on public
 * marketing routes.
 *
 * Autocapture is set to `false` at init (the safe default for the marketing
 * site, where blanket click/interaction capture buys little product signal).
 * On the authenticated side, where interaction data is genuinely useful for
 * understanding feature usage — and inputs are already fully masked in replay
 * and never captured by autocapture — we re-enable it at runtime.
 */
export const applyAutocapture = (pathname: string | null): void => {
  if (!hasWindow() || !isPosthogLoaded()) {
    return;
  }

  posthog.set_config({
    autocapture: !isPublicMarketingRoute(pathname) && !isAdminRoute(pathname),
  });
};

export { posthog };
